import * as React from "react";
import { Alert, Button, Space, Tooltip } from "antd";
import { RefreshCw, Trash2, Clock, Loader, HelpCircle } from "react-feather";
import { Table } from "../../newcomponents/table/table";
import { useGlobalJobExecutions } from "./useGlobalJobExecutions";
import { getJobExecutionsColumns } from "./columns";
import { useAppSelector } from "../../store/hooks";
import { selectOrg } from "../../store/contextSlice";
import { selectLabel } from "../../store/timezoneSlice";

const searchableFields = [
  "Workload name",
  "GVC name",
  "Execution name",
  "Command name",
  "Location",
  "Status",
];

const SearchHelpTooltip: React.FC = () => (
  <Tooltip
    title={
      <div>
        <div className="font-medium mb-1">Searchable fields:</div>
        <ul className="m-0 pl-4">
          {searchableFields.map((field) => (
            <li key={field}>{field}</li>
          ))}
        </ul>
      </div>
    }
  >
    <HelpCircle size={16} style={{ color: "var(--color-label)", cursor: "help" }} />
  </Tooltip>
);

export const JobExecutionsList: React.FC = () => {
  const org = useAppSelector(selectOrg);
  const timezoneLabel = useAppSelector(selectLabel);

  const {
    executions,
    isLoading,
    error,
    progress,
    cacheAge,
    totalWorkloads,
    fetchExecutions,
    clearLocalCache,
  } = useGlobalJobExecutions(org);

  const columns = React.useMemo(() => getJobExecutionsColumns(org, timezoneLabel), [org, timezoneLabel]);

  // Auto-fetch on mount if no cached data exists
  const hasFetchedRef = React.useRef(false);
  React.useEffect(() => {
    if (!hasFetchedRef.current && cacheAge === "Never" && !isLoading) {
      hasFetchedRef.current = true;
      fetchExecutions();
    }
  }, [cacheAge, isLoading, fetchExecutions]);

  const handleRefresh = () => {
    fetchExecutions();
  };

  const handleClearCache = () => {
    clearLocalCache();
  };

  const showProgress = progress.phase !== "idle" && isLoading;

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* Header Stats and Actions - only show when we have data */}
      {(executions.length > 0 || isLoading) && (
        <div className="flex items-center justify-between mb-4 flex-wrap gap-4">
          <div className="flex items-center gap-4 text-sm" style={{ color: "var(--color-label)" }}>
            {showProgress ? (
              <div className="flex items-center gap-2">
                <Loader size={14} className="animate-spin" />
                <span>{progress.message}</span>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-1">
                  <Clock size={14} />
                  <span>Last updated: {cacheAge}</span>
                </div>
                {totalWorkloads > 0 && (
                  <>
                    <span>|</span>
                    <span>{totalWorkloads} cron workloads</span>
                    <span>|</span>
                    <span>{executions.length} executions</span>
                  </>
                )}
              </>
            )}
          </div>
          <Space>
            <Button icon={<RefreshCw size={14} />} onClick={handleRefresh} loading={isLoading} disabled={isLoading}>
              Refresh
            </Button>
            <Button
              icon={<Trash2 size={14} />}
              onClick={handleClearCache}
              disabled={isLoading || executions.length === 0}
            >
              Clear Cache
            </Button>
          </Space>
        </div>
      )}

      {/* Error Alert */}
      {error && (
        <Alert message="Error" description={error} type="error" showIcon closable className="mb-4" />
      )}

      {/* Initial State - No Data */}
      {!isLoading && executions.length === 0 && !error && (
        <div className="flex flex-col items-center justify-center py-16">
          <div className="text-xl mb-2">No Job Executions</div>
          <div className="text-sm mb-4" style={{ color: "var(--color-label)" }}>
            Load job executions from all cron workloads in this organization.
          </div>
          <Button type="primary" icon={<RefreshCw size={14} />} onClick={handleRefresh} loading={isLoading}>
            Load Job Executions
          </Button>
        </div>
      )}

      {/* Table */}
      {(executions.length > 0 || isLoading) && (
        <div className="job-executions-table-wrapper">
          <Table
            tableId={`global-job-executions-${org}`}
            title="Job Executions"
            data={executions}
            columns={columns}
            isLoading={isLoading && executions.length === 0}
            enableFilter
            filterPrefix={<SearchHelpTooltip />}
            hideSettings
            classNames={{ container: "flex-1 min-h-0" }}
          />
        </div>
      )}
    </div>
  );
};
